import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, GridContainer, GridChild, Title, CustomBox } from 'common';
import { Alert } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { CourseCard } from 'widgets/cards/courseCard';
import { Course, useCourse } from 'api/courses';
import { Helmet } from 'react-helmet';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import { CoursesImagesCarousel } from './CoursesImagesCarousel'

import { useLanguage } from '../../context/LanguageContext'
import { courseSinglePageLabels } from '../../locales/translations'

import dayjs from 'dayjs'; // Ensure dayjs is installed via yarn add dayjs

export const SingleCoursePage = () => {
  const { language } = useLanguage();
  const { id } = useParams();
  const { onGetOneCourse, onGetAllCourse } = useCourse();
  const [courseDetails, setCourseDetails] = useState<Course | null>(null);
  const [latestCourses, setLatestCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true; // Flag to track mounted state of the component

    const fetchCourseDetails = async () => {
      try {
        const courseResult = await onGetOneCourse(Number(id));
        if (courseResult.success) {
          if (isMounted) {
            setCourseDetails(courseResult.data);
          }
          return courseResult.data.category; // Return the category for chaining
        } else {
          if (isMounted) {
            setError('Не удалось загрузить детали курса.');
          }
        }
      } catch (error) {
        if (isMounted) {
          setError('Произошла ошибка при запросе деталей курса.');
        }
        throw error; // Rethrow the error to handle it in the chained catch block
      }
    };

    const fetchLatestCourses = async (category: string) => {
      try {
        const coursesResult = await onGetAllCourse();
        if (coursesResult.success) {
          const filteredCourses = coursesResult.data
            .filter((course: Course) => course.category === category)
            .sort((a: Course, b: Course) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 3); // Get the latest three courses
          if (isMounted) {
            setLatestCourses(filteredCourses);
          }
        } else {
          if (isMounted) {
            setError('Произошла ошибка при загрузке списка курсов.');
          }
        }
      } catch (error) {
        if (isMounted) {
          setError('Произошла ошибка при запросе списка курсов.');
        }
        throw error;
      }
    };

    setLoading(true); // Start loading state
    fetchCourseDetails()
      .then(category => {
        if (category && isMounted) {
          return fetchLatestCourses(category);
        }
      })
      .catch(error => console.error(error)) // Handle any errors in the chain
      .finally(() => {
        if (isMounted) {
          setLoading(false); // End loading state
        }
      });

    return () => {
      isMounted = false; // Clean up the mounted state on unmount
    };
  }, [id, onGetOneCourse, onGetAllCourse]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Render the content of the page with conditional rendering based on data fetched
  return (
    <Box>
      <GridContainer>
        <GridChild md={8}>
          {courseDetails && (
            <CustomBox title={courseDetails[`title_${language}`]}>
              <Helmet>
                <title>{courseDetails[`title_${language}`]}</title>
                <meta name="description" content="Информация о курсе" />
              </Helmet>
              {courseDetails.images && courseDetails.images.length > 0 && courseDetails.images[0] ? (
                <CoursesImagesCarousel coursesItem={courseDetails} />
              ) : (<ImageNotSupportedIcon
                sx={{
                  height: 200,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '5rem'
                }}
              />)}
              <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                {courseDetails[`title_${language}`]}
              </Title>
              <Title gutterBottom variant="body2" color="text.secondary">
                <div dangerouslySetInnerHTML={{
                  __html: courseDetails[`description_${language}`]
                }} />
              </Title>
              <Title variant="body2" color="text.primary">
                {dayjs(courseDetails.created_at).format('DD.MM.YYYY')}
              </Title>
            </CustomBox>
          )}
        </GridChild>

        <GridChild md={4}>
          <CustomBox title={courseSinglePageLabels.latestCourses[language]}>
            {latestCourses.map((courseItem) => (
              <CourseCard key={courseItem.id} coursesItem={courseItem} />
            ))}
            <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'start' }}>
              <Button variant="contained" color="error" href="/courses">
                {courseSinglePageLabels.allCoursesButton[language]}
              </Button>
            </Box>
          </CustomBox>
        </GridChild>
      </GridContainer>
    </Box>
  );
};
